import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./OrdersPagination.css";
import { Link } from "../../components/Link";
import { Button } from "../../components/Button";
import { useLocation } from "react-router-dom";
import { payment, service } from "../../store";
import searchIcon from "../../icons/search.svg"
import leftArrowButton from "../../icons/chevron-left-large.svg"
import rightArrowButton from "../../icons/chevron-right-large.svg"

// 결제 상태(status) → 한글 표시 라벨 매핑.
// 일부 status는 동일 라벨로 묶인다(WAITING_FOR_DEPOSIT/COMPLETED → 결제확인).
const STATUS_LABEL_MAP = {
  CANCELED: "결제취소",
  READY: "결제중",
  DONE: "결제완료",
  WAITING_FOR_DEPOSIT: "결제확인", //"입금대기",
  COMPLETED: "결제확인",
  IN_PROGRESS: "처리중",
  PARTIAL_CANCELED: "부분취소",
  ABORTED: "실패",
  EXPIRED: "만료",
};

// 필터 드롭다운에 노출할 고유 라벨 목록(표시 순서).
const STATUS_FILTER_OPTIONS = [
  "결제완료",
  "결제확인",
  "결제중",
  "처리중",
  "부분취소",
  "결제취소",
  "실패",
  "만료",
];

export const OrdersPagination = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLink, setCurrentLink] = useState("/mypage/orders/");
  const postsPerPage = 7;
  const getPayments = payment((state) => state.getPayments);
  const payments = payment((state) => state.payments);
  const isLoading = payment((state) => state.isLoading);
  const getPaymentByUser = payment((state) => state.getPaymentByUser);
  const clearPayments = payment((state) => state.clearPayments);
  const categories = service((state) => state.categories || []);
  const getCategories = service((state) => state.getCategories);
  const searchPayments = payment((state) => state.searchPayments);
  const searchUserPayments = payment((state) => state.searchUserPayments);
  const sortbyCategoryPayments = payment((state) => state.sortbyCategoryPayments);
  const sortUserPaymentsByCategory = payment((state) => state.sortUserPaymentsByCategory);
  const syncPayments = payment((state) => state.syncPayments);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [error, setError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const location = useLocation();
  const isAdmin = location.pathname === "/admin/orders";

  const myUserId = useMemo(() => {
    const data = localStorage.getItem("auth-storage");
    return data ? JSON.parse(data).state?.user?.userId : null;
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleCategoryChange = useCallback((e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((e) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchKeyword(e.target.value);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      clearPayments();
      if (location.pathname === "/admin/orders") {
        setCurrentLink("/admin/orders/");
        if (searchKeyword) {
          await searchPayments(searchKeyword);
        } else if (selectedCategory) {
          await sortbyCategoryPayments(selectedCategory);
        } else {
          await getPayments();
        }
      } else if (location.pathname === "/mypage/orders" && myUserId) {
        setCurrentLink("/mypage/orders/");
        if (searchKeyword) {
          await searchUserPayments(myUserId, searchKeyword);
        } else if (selectedCategory) {
          await sortUserPaymentsByCategory(myUserId, selectedCategory);
        } else {
          await getPaymentByUser(myUserId);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("데이터를 불러오는 중 오류가 발생했습니다.");
    }
  }, [location.pathname, myUserId, searchKeyword, selectedCategory, clearPayments, searchPayments, sortbyCategoryPayments, getPayments, searchUserPayments, sortUserPaymentsByCategory, getPaymentByUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 토스와 결제 상태 일괄 대조 (관리자 전용)
  // 결제 건수만큼 토스 API를 순차 호출하므로 수 초~수십 초 걸릴 수 있어
  // isSyncing으로 버튼을 비활성화해 중복 호출을 막는다.
  const handleSync = useCallback(async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const res = await syncPayments();
      const changed = res?.changed ?? 0;
      if (changed > 0) {
        const changedOrderIds = (res?.changes || [])
          .map((c) => c?.order_id)
          .filter(Boolean);
        alert(
          `${changed}건의 결제 상태가 토스 기준으로 갱신되었습니다.${
            changedOrderIds.length
              ? `\n(주문번호: ${changedOrderIds.join(", ")})`
              : ""
          }`
        );
        await fetchData();
      } else {
        alert("모든 결제가 토스와 일치합니다.");
      }
    } catch (error) {
      console.error("결제 동기화 실패:", error);
      alert("동기화에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, syncPayments, fetchData]);

  // 상태 필터는 클라이언트 사이드에서 적용한다(payments는 이미 전체가 로드됨).
  // 선택된 라벨과 매칭되는 status만 남긴다. WAITING_FOR_DEPOSIT/COMPLETED처럼
  // 여러 status가 같은 라벨을 공유하는 경우도 라벨 기준으로 함께 필터된다.
  const filteredPayments = useMemo(() => {
    if (!selectedStatus) return payments;
    return payments.filter(
      (p) => STATUS_LABEL_MAP[p.status] === selectedStatus
    );
  }, [payments, selectedStatus]);

  const totalPosts = filteredPayments?.length || 0;

  const currentPosts = useMemo(() => {
    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    return filteredPayments.slice(indexOfFirstPost, indexOfLastPost);
  }, [filteredPayments, currentPage, postsPerPage]);

  const renderPageButtons = useCallback(() => {
    const pageButtons = [];
    const totalPages = Math.ceil(totalPosts / postsPerPage);
    let startPage = Math.max(currentPage - 2, 1);
    let endPage = Math.min(startPage + 4, totalPages);

    if (endPage - startPage < 4) {
      startPage = Math.max(endPage - 4, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          className={
            currentPage === i
              ? "order-pagination-page-button active"
              : "order-pagination-page-button"
          }
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return pageButtons;
  }, [currentPage, totalPosts, postsPerPage, handlePageChange]);

  const getStateClassName = useCallback((state) => {
    return STATUS_LABEL_MAP[state] || "";
  }, []);

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="order-pagination-container">
      <div className="order-pagination-sort-wrap">
        <div className="order-pagination-search-input-wrap">
          <img src={searchIcon} className="order-pagination-search-icon" alt="검색 이미지" />
          <input
            type="search"
            className="order-pagination-search-input"
            placeholder="Search..."
            value={searchKeyword}
            onChange={handleSearchChange}
          />
        </div>
        <div className="order-category">
          <select
            id="order-category-select"
            name="order-category-select" 
            value={selectedCategory}
            onChange={handleCategoryChange}  
          >
            <option value="">전체 카테고리</option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
          >
            <path
              d="M3.06559 4.5061L2.12695 5.44474L7.37695 10.6947L12.627 5.44474L11.6883 4.5061L7.37695 8.81744L3.06559 4.5061Z"
              fill="#9095a1"
            />
          </svg>
        </div>
        <div className="order-category order-status">
          <select
            id="order-status-select"
            name="order-status-select"
            value={selectedStatus}
            onChange={handleStatusChange}
          >
            <option value="">전체 상태</option>
            {STATUS_FILTER_OPTIONS.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
          >
            <path
              d="M3.06559 4.5061L2.12695 5.44474L7.37695 10.6947L12.627 5.44474L11.6883 4.5061L7.37695 8.81744L3.06559 4.5061Z"
              fill="#9095a1"
            />
          </svg>
        </div>
        {isAdmin && (
          <Button
            label={isSyncing ? "동기화 중..." : "토스와 동기화"}
            variant="primary"
            size="sm"
            onClick={handleSync}
            className={`order-pagination-sync-button${isSyncing ? " disabled" : ""}`}
            style={{ marginLeft: "auto" }}
            type="button"
          />
        )}
      </div>
      <div className="order-list">
        <div className="order-list-header">
          <div></div>
          <div>상태</div>
          <div className="order-pagination-mobile-hide">결제ID</div>
          <div>상품명</div>
          <div className="order-pagination-mobile-hide">카테고리</div>
          <div className="order-pagination-mobile-hide">결제금액</div>
          <div>결제일자</div>
        </div>
        {isLoading ? (
        <div className="order-item">
          <div></div>
          <div>Loading...</div>
        </div>
        ) : ( currentPosts.map((post) => (
          <div key={post.order_id} className="order-item">
            <div>
              <input type="checkbox" name={post.order_id} id={post.order_id} />
            </div>
            <div
              className={`order-item-state ${post.status}`}
            >
              {getStateClassName(post.status)}
            </div>
            <div className="order-pagination-mobile-hide">#{post.order_id}</div>
            <Link
              to={`${currentLink}${post.order_id}`}
              className="post-item-link"
              style={{
                backgroundColor: "transparent",
                fontFamily: '"Lexend", Helvetica',
                fontWeight: "700",
                fontSize: "14px",
                color: "#dee1e6",
                width: "auto",
                height: "18px",
                textAlign: "left",
                display: "block",
              }}
            >
              <div>{post.course_title}</div>
            </Link>
            <div className="order-pagination-mobile-hide">{post.category_name}</div>
            <div className="order-pagination-mobile-hide">₩{(post.amount).toLocaleString()}</div>
            <div>{new Date(post.created_at).toLocaleDateString("ko-KR")}</div>
          </div>
        )))}
      </div>
      <div className="order-pagination-footer">
        <div className="order-pagination-count">{totalPosts} results</div>
        <div className="order-pagination-buttons">
          <button
            className={`order-pagination-button ${
              currentPage === 1 ? "disabled" : ""
            }`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <img
              className="img-11"
              alt="Chevron left large"
              src={leftArrowButton}
            />
          </button>
          <div className="order-pagination-button-wrap">
            {renderPageButtons()}
          </div>
          <button
            className={`order-pagination-button ${
              currentPage === Math.ceil(totalPosts / postsPerPage)
                ? "disabled"
                : ""
            }`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === Math.ceil(totalPosts / postsPerPage)}
          >
            <img
              className="img-11"
              alt="Chevron right large"
              src={rightArrowButton}
            />
          </button>
        </div>
      </div>
    </div>
  );
};